import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  useLazyFetchFindoraInventoryListQuery,
} from '../productMappingSchemaListApiSlice'
import { splitToManyInventory } from 'features/public-views/manageStock/API/manageStockAction'
import { newFormatThousandSeparator } from 'utils/formatNumber'

const TOLERANCE = 0.0001
// 3 decimals, matching how the backend rounds schema reqAmount
const roundNum = (num) => Math.round((Number(num) + Number.EPSILON) * 1000) / 1000

export const useBulkAutoPopulate = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [isProcessing, setIsProcessing] = useState(false)

  const [fetchInventory] = useLazyFetchFindoraInventoryListQuery()

  // Helpers
  const getWarehouseId = (inv) => inv?.warehouseId ?? inv?.customLabel?.warehouseId ?? null
  const getWarehouseName = (inv) => inv?.warehouseName || inv?.customLabel?.warehouseName || '-'

  const matchesWarehouse = (inv, targetWarehouse) => {
    if (!targetWarehouse) return true
    const targetId = typeof targetWarehouse === 'object' ? targetWarehouse?.id : targetWarehouse
    const targetName = typeof targetWarehouse === 'object' ? targetWarehouse?.name : targetWarehouse

    const invId = getWarehouseId(inv)
    const invName = getWarehouseName(inv)

    if (targetId && invId && String(invId).trim().toLowerCase() === String(targetId).trim().toLowerCase()) return true
    if (targetName && invName && invName !== '-' && String(invName).trim().toLowerCase() === String(targetName).trim().toLowerCase()) return true
    return false
  }

  const buildOption = (inventory, recipe) => {
    const itemName = inventory.item?.nameItem || inventory.nameItem || recipe.nameItem || '-'
    const ownerCode = inventory.ownerCode || '-'
    const lotCode = inventory.lotCode || '-'
    const amount = inventory.amount || inventory.amountNum || 0
    const unit = inventory.unit || recipe.unit || 'Kg'
    const warehouseName = inventory.warehouseName || '-'
    const groupName = inventory.groupLabel || '-'
    const label = `${itemName} | ID: ${inventory.id} | Kode Inventori: ${ownerCode} | Lot ID: ${lotCode} | ${amount} ${unit} | Gudang: ${warehouseName} | Grup: ${groupName}`
    return {
      value: inventory.id,
      label: label,
      image: inventory.item?.linkFoto || inventory.linkFoto || recipe.image || null,
      customLabel: {
        id: inventory.id,
        itemName, ownerCode, lotCode, amount, unit,
        warehouseId: inventory.warehouseId,
        warehouseName: inventory.warehouseName,
        groupName,
      },
      ...inventory,
    }
  }

  const findExactSubset = (items, target, tolerance = 0.0001) => {
    const sorted = [...items].sort((a, b) => b.amountNum - a.amountNum)
    const search = (itemIndex, currentSum, selected) => {
      if (Math.abs(currentSum - target) <= tolerance) return selected
      if (currentSum > target + tolerance || itemIndex >= sorted.length) return null
      const withCurrent = search(itemIndex + 1, currentSum + sorted[itemIndex].amountNum, [...selected, sorted[itemIndex]])
      if (withCurrent) return withCurrent
      return search(itemIndex + 1, currentSum, selected)
    }
    return search(0, 0, [])
  }

  const processAutoPopulateRecipe = async (recipe, requiredAmount, usedInventoryIds, activeWarehouse, allowPartial = false) => {
    const materialItemId = recipe.itemId || recipe.item?.id || recipe.id
    const inventoryRes = await fetchInventory({ itemIds: materialItemId })
    const latestInventoryList = inventoryRes?.data

    if (!latestInventoryList || latestInventoryList.length === 0) {
      throw new Error(`Tidak ada stok inventori yang tersedia untuk material ${recipe.nameItem || recipe.item?.nameItem || '-'}.`)
    }

    const targetUnit = String(recipe.unit || 'Kg').trim().toLowerCase()
    const unitMatchingInvs = latestInventoryList
      .filter((inv) => {
        const invUnit = String(inv.unit || inv.customLabel?.unit || '').trim().toLowerCase()
        return invUnit === targetUnit && Number(inv.amount || 0) > 0 && !usedInventoryIds.includes(inv.id)
      })
      .map((inv) => ({ ...inv, amountNum: Number(inv.amount || 0) }))

    if (unitMatchingInvs.length === 0) {
      throw new Error(`Tidak ada stok inventori dengan satuan "${recipe.unit || 'Kg'}" yang tersedia untuk material ${recipe.nameItem || recipe.item?.nameItem || '-'}.`)
    }

    const warehouseGroups = {}
    if (activeWarehouse) {
      const matchingInvs = unitMatchingInvs.filter((inv) => matchesWarehouse(inv, activeWarehouse))
      if (matchingInvs.length === 0) {
        throw new Error(`Tidak ada stok di gudang yang sama untuk material ${recipe.nameItem || recipe.item?.nameItem || '-'}.`)
      }
      const groupKey = (typeof activeWarehouse === 'object' ? activeWarehouse?.id || activeWarehouse?.name : activeWarehouse) || 'activeWarehouse'
      warehouseGroups[groupKey] = matchingInvs
    } else {
      unitMatchingInvs.forEach((inv) => {
        const wh = getWarehouseId(inv) || getWarehouseName(inv)
        if (!warehouseGroups[wh]) warehouseGroups[wh] = []
        warehouseGroups[wh].push(inv)
      })
    }

    let bestSolution = null

    for (const whKey of Object.keys(warehouseGroups)) {
      const whInvs = warehouseGroups[whKey]

      const exactMatch = whInvs.find((inv) => Math.abs(inv.amountNum - requiredAmount) <= TOLERANCE)
      if (exactMatch) {
        bestSolution = { type: 'EXACT_SINGLE', inventory: exactMatch, priority: 1 }
        break
      }

      const exactSubset = findExactSubset(whInvs, requiredAmount, TOLERANCE)
      if (exactSubset && exactSubset.length > 0) {
        if (!bestSolution || bestSolution.priority > 2) {
          bestSolution = { type: 'EXACT_SUBSET', subset: exactSubset, priority: 2 }
        }
      }

      const largerInvs = whInvs.filter((inv) => inv.amountNum > requiredAmount + TOLERANCE).sort((a, b) => a.amountNum - b.amountNum)
      if (largerInvs.length > 0) {
        const targetInv = largerInvs[0]
        const remainder = roundNum(targetInv.amountNum - requiredAmount)
        if (!bestSolution || bestSolution.priority > 3) {
          bestSolution = { type: 'SINGLE_SPLIT', targetInv, remainder, priority: 3 }
        }
      }

      const sortedDesc = [...whInvs].sort((a, b) => b.amountNum - a.amountNum)
      let collectedAmount = 0
      const entriesToSet = []
      const intactInvsForPlan = []
      let splitInfo = null

      for (let i = 0; i < sortedDesc.length; i++) {
        const inv = sortedDesc[i]
        const neededRemaining = roundNum(requiredAmount - collectedAmount)
        if (neededRemaining <= TOLERANCE) break
        
        if (inv.amountNum <= neededRemaining + TOLERANCE) {
          entriesToSet.push({ inventoryId: buildOption(inv, recipe), amount: inv.amountNum })
          intactInvsForPlan.push(inv)
          collectedAmount = roundNum(collectedAmount + inv.amountNum)
        } else {
          const remainder = roundNum(inv.amountNum - neededRemaining)
          splitInfo = { targetInv: inv, splitAmount: neededRemaining, remainderAmount: remainder, intactInvs: intactInvsForPlan, existingEntries: entriesToSet }
          collectedAmount = roundNum(collectedAmount + neededRemaining)
          break
        }
      }

      if (splitInfo && (!bestSolution || bestSolution.priority > 4)) {
        bestSolution = { type: 'COMBINE_SPLIT', splitInfo, priority: 4 }
      } else if (!bestSolution && entriesToSet.length > 0 && collectedAmount > 0) {
        bestSolution = { type: 'INSUFFICIENT_COMBINE', entriesToSet, collectedAmount, priority: 5 }
      }
    }

    if (!bestSolution) {
      throw new Error(`Gagal menemukan inventori yang cocok untuk ${recipe.nameItem || recipe.item?.nameItem || '-'}.`)
    }

    if (bestSolution.type === 'EXACT_SINGLE') {
      return { status: 'RESOLVED', entries: [{ inventoryId: buildOption(bestSolution.inventory, recipe), amount: requiredAmount }] }
    }

    if (bestSolution.type === 'EXACT_SUBSET') {
      return { status: 'RESOLVED', entries: bestSolution.subset.map((inv) => ({ inventoryId: buildOption(inv, recipe), amount: inv.amountNum })) }
    }

    if (bestSolution.type === 'SINGLE_SPLIT') {
      const splitId = `split_${Date.now()}_${Math.random()}`
      return {
        status: 'PENDING_SPLIT',
        splitPlan: {
          id: splitId,
          targetInv: bestSolution.targetInv,
          splitAmount: roundNum(requiredAmount),
          remainderAmount: roundNum(bestSolution.remainder),
          recipe,
          materialItemId
        },
        entries: [{ inventoryId: `PENDING_SPLIT_${splitId}`, amount: roundNum(requiredAmount) }]
      }
    }

    if (bestSolution.type === 'COMBINE_SPLIT') {
      const { targetInv, splitAmount, remainderAmount, existingEntries } = bestSolution.splitInfo
      const splitId = `split_${Date.now()}_${Math.random()}`
      return {
        status: 'PENDING_SPLIT',
        splitPlan: {
          id: splitId,
          targetInv,
          splitAmount: roundNum(splitAmount),
          remainderAmount: roundNum(remainderAmount),
          recipe,
          materialItemId
        },
        entries: [...existingEntries, { inventoryId: `PENDING_SPLIT_${splitId}`, amount: roundNum(splitAmount) }]
      }
    }

    if (bestSolution.type === 'INSUFFICIENT_COMBINE') {
      // Substitutes in an Atau group may take what is in stock and leave the rest to the next substitute
      if (allowPartial) return { status: 'RESOLVED', entries: bestSolution.entriesToSet }
      throw new Error(`Stok inventori di gudang hanya mencukupi ${bestSolution.collectedAmount} ${recipe.unit || 'Kg'} dari ${requiredAmount} ${recipe.unit || 'Kg'} yang dibutuhkan untuk material ${recipe.nameItem || recipe.item?.nameItem || '-'}.`)
    }
  }

  // Calculate entries for all recipes. With a schema rule, substitutes in an Atau / Salah satu group share one need
  // (the largest member requirement): Atau fills it member by member (partial stock allowed), Salah satu uses the
  // first member that can cover it alone.
  const calculateBulkAutoPopulate = useCallback(async (selectedItem, recipeGroups, rule = null, options = {}) => {
    try {
      setIsProcessing(true)

      let usedInventoryIds = options?.usedInventoryIds ? [...options.usedInventoryIds] : []
      let activeWarehouse = options?.activeWarehouse || null
      const allPendingSplits = []
      const qty = Number(selectedItem?.totalQty ?? 1)
      const requiredOf = (recipe) => roundNum(Number(recipe.reqAmount ?? 0) * qty)
      const emptyEntries = (amount) => [{ id: Date.now() + Math.random(), inventoryId: null, amount }]
      const nameOf = (recipe) => recipe.nameItem || recipe.item?.nameItem || '-'

      // Fill one recipe up to `amount`, tracking used inventories and the shared warehouse
      const fill = async (recipe, amount, allowPartial) => {
        const result = await processAutoPopulateRecipe(recipe, amount, usedInventoryIds, activeWarehouse, allowPartial)
        const filled = roundNum(result.entries.reduce((sum, e) => sum + Number(e.amount || 0), 0))
        // Never take more than asked; rejected before any lot/split is recorded, so substitutes can't double-count
        if (filled > amount + TOLERANCE) {
          throw new Error(`Alokasi ${nameOf(recipe)} melebihi kebutuhan.`)
        }
        const resolvedInvIds = result.entries
          .filter((e) => e.inventoryId && typeof e.inventoryId !== 'string')
          .map((e) => e.inventoryId.value)
        usedInventoryIds = [...usedInventoryIds, ...resolvedInvIds]

        if (result.status === 'PENDING_SPLIT' && result.splitPlan) {
          allPendingSplits.push(result.splitPlan)
          // track the targetInv being split so it's not reused
          usedInventoryIds.push(result.splitPlan.targetInv.id)
        }

        if (!activeWarehouse) {
          const firstResolved = result.entries.find((e) => e.inventoryId && typeof e.inventoryId !== 'string')
          const whId = firstResolved
            ? firstResolved.inventoryId?.customLabel?.warehouseId
            : result.splitPlan && getWarehouseId(result.splitPlan.targetInv)
          const whName = firstResolved
            ? firstResolved.inventoryId?.customLabel?.warehouseName
            : result.splitPlan && getWarehouseName(result.splitPlan.targetInv)
          if (whId || whName) {
            activeWarehouse = { id: whId ? String(whId) : null, name: whName ? String(whName) : null }
          }
        }

        return {
          entries: result.entries.map((entry) => ({
            id: Date.now() + Math.random(),
            inventoryId: entry.inventoryId,
            amount: entry.amount,
          })),
          filled,
        }
      }

      const sharedGroups = (rule?.groups || [])
        .filter((g) => g.op !== 'AND')
        .map((g) => ({
          op: g.op,
          members: recipeGroups
            .map((r, i) =>
              g.itemIds.includes(Number(r.itemId ?? r.id)) ? i : -1,
            )
            .filter((i) => i >= 0),
        }))
        .filter(
          (g) =>
            g.members.length > 1 ||
            (recipeGroups.length === 1 && g.members.length === 1),
        )

      const results = recipeGroups.map(() => null)

      for (let i = 0; i < recipeGroups.length; i++) {
        if (results[i]) continue
        const group = sharedGroups.find((g) => g.members.includes(i))

        if (!group) {
          const recipe = recipeGroups[i]
          const requiredAmount = requiredOf(recipe)
          results[i] = emptyEntries(requiredAmount)
          if (requiredAmount > 0) {
            try {
              results[i] = (await fill(recipe, requiredAmount, false)).entries
            } catch (e) {
              enqueueSnackbar(e.message || `Gagal mengisi ${nameOf(recipe)}`, { variant: 'warning' })
            }
          }
          continue
        }

        const need = Math.max(...group.members.map((m) => requiredOf(recipeGroups[m])))
        const names = group.members.map((m) => nameOf(recipeGroups[m])).join(' / ')
        group.members.forEach((m) => {
          results[m] = emptyEntries(requiredOf(recipeGroups[m]))
        })
        if (need <= 0) continue

        if (group.op === 'XOR') {
          let picked = false
          for (const m of group.members) {
            try {
              results[m] = (await fill(recipeGroups[m], need, false)).entries
              picked = true
              break
            } catch {
              // try the next substitute
            }
          }
          if (!picked) {
            enqueueSnackbar(`Tidak ada satu pun dari ${names} yang stoknya cukup untuk ${newFormatThousandSeparator(need, 3)}.`, { variant: 'warning' })
          }
          continue
        }

        let remaining = need
        for (const m of group.members) {
          if (remaining <= TOLERANCE) break
          try {
            const { entries, filled } = await fill(recipeGroups[m], remaining, true)
            results[m] = entries
            remaining = Math.max(0, roundNum(remaining - filled))
          } catch {
            // no stock for this substitute, let the next one cover it
          }
        }
        if (remaining > TOLERANCE) {
          enqueueSnackbar(`Stok gabungan ${names} masih kurang ${newFormatThousandSeparator(remaining, 3)}.`, { variant: 'warning' })
        }
      }

      const updatedRecipeGroups = recipeGroups.map((recipe, i) => ({ ...recipe, entries: results[i] }))

      return {
        requiresConfirmation: allPendingSplits.length > 0,
        pendingSplits: allPendingSplits,
        pendingRecipeGroups: updatedRecipeGroups,
      }
    } catch (error) {
      console.error('Bulk Auto Populate calculate error:', error)
      enqueueSnackbar(error?.message || error || 'Gagal memproses Auto Populate massal.', { variant: 'error' })
      return null
    } finally {
      setIsProcessing(false)
    }
  }, [fetchInventory, enqueueSnackbar])

  const executeBulkSplits = useCallback(async (pendingSplits, pendingRecipeGroups) => {
    try {
      setIsProcessing(true)
      const splitResultsMap = {}

      for (const splitPlan of pendingSplits) {
        const { id, targetInv, splitAmount, remainderAmount, recipe, materialItemId } = splitPlan
        const payload = {
          inventoryId: targetInv.id,
          arrNew: [
            {
              amount: roundNum(splitAmount),
              itemId: targetInv.itemId || materialItemId,
              unit: targetInv.unit || recipe.unit || 'Kg',
              ...(targetInv?.bundle && { bundle: Number(targetInv.bundle) }),
            },
            {
              amount: roundNum(remainderAmount),
              itemId: targetInv.itemId || materialItemId,
              unit: targetInv.unit || recipe.unit || 'Kg',
              ...(targetInv?.bundle && { bundle: Number(targetInv.bundle) }),
            },
          ],
          notes: `auto-split-pemetaan-${targetInv.id}-${Date.now()}`,
        }

        const splitResult = await dispatch(splitToManyInventory(payload)).unwrap()
        const newInventories = Array.isArray(splitResult) ? splitResult : splitResult?.items || splitResult?.data || splitResult?.newInventories || splitResult?.result || []
        
        if (newInventories && newInventories.length > 0) {
          const selectedSplitInv = newInventories[0]
          splitResultsMap[`PENDING_SPLIT_${id}`] = buildOption(
            { ...targetInv, ...selectedSplitInv },
            recipe,
          )
        } else {
          throw new Error(`Gagal melakukan split inventori untuk ${recipe.nameItem}`)
        }
      }

      // Replace placeholders in recipe groups
      const finalRecipeGroups = pendingRecipeGroups.map(group => {
        return {
          ...group,
          entries: group.entries.map(entry => {
            if (typeof entry.inventoryId === 'string' && entry.inventoryId.startsWith('PENDING_SPLIT_')) {
              return {
                ...entry,
                inventoryId: splitResultsMap[entry.inventoryId] || null
              }
            }
            return entry
          })
        }
      })

      return finalRecipeGroups
    } catch (error) {
      console.error('Execute Bulk Splits error:', error)
      enqueueSnackbar(error?.message || error || 'Gagal mengeksekusi split inventori massal.', { variant: 'error' })
      return null
    } finally {
      setIsProcessing(false)
    }
  }, [dispatch, enqueueSnackbar])

  return { calculateBulkAutoPopulate, executeBulkSplits, isProcessing }
}
